import { useState, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, AlertCircle, AlertTriangle, Printer, X } from 'lucide-react'
import { useApp } from '../App'
import { menusAPI, airtableAPI } from '../lib/api'
import { useToastActions } from '../components/Toast'
import { heatBanner } from '../lib/customsHeat'

// Meal-type keys (from the Airtable import) → matched to a dish by dish.category.
const MEAL_CATEGORIES = ['chicken', 'chicken_2', 'beef', 'seafood', 'seafood_2', 'veg', 'cheat', 'pasta', 'salad', 'salad_2']
// 'base' = a plated topping/finisher (e.g. a salad's toasted pumpkin seeds, dried
// apricots, goat cheese). It's a real ingredient on the plate, so it must render as a
// line and be removable — otherwise salad toppings vanish and "no goat cheese" is a
// silent no-op. (Sides like oats/chia also use 'base', but they never become cards.)
const WEIGHED_TYPES = ['protein', 'vegetable', 'carb', 'sauce', 'base']

const TYPE_LABEL = { protein: 'Protein', vegetable: 'Vegetable', carb: 'Carb', sauce: 'Sauce', base: 'Topping' }
const TYPE_ORDER = { protein: 1, vegetable: 2, carb: 3, sauce: 4, base: 5 }

// ── Portion rules (your spec) — computed from each dish's standard portion ──────
//   extra / double = regular + 1 oz   ·   low = half   ·   none = 0   ·   regular = standard
const PORTION_RULE = {
  regular: (oz) => oz,
  extra: (oz) => oz + 1,
  double: (oz) => oz + 1,
  low: (oz) => oz * 0.5,
  none: () => 0,
  side: (oz) => oz, // full portion — the sauce is still made, just served in a separate container
}
const fmtOz = (oz) => (oz == null || Number.isNaN(Number(oz)) ? '—' : `${Math.round(Number(oz) * 10) / 10} oz`)
const norm = (s) => (s || '').toLowerCase().trim()

// Is a swap's named replacement actually available on this week's menu? The match is
// substring/token-aware, NOT exact: a customer often names a replacement generically
// ("swap protein for chicken") that isn't a literal component name — the chicken this
// week is plated as "Butter Chicken" / "Blackened Chicken Breast". Treat the target as
// available when the named term matches a real component either way (term ⊆ component
// name, or component name ⊆ term). A genuinely off-menu name (e.g. "Cauliflower" on a
// week with no cauliflower) still matches nothing → the caller omits it, so the "don't
// plate what we don't have" protection is preserved.
function swapTargetOnMenu(pool, to) {
  const t = norm(to)
  if (!t) return false
  return (pool || []).some(o => {
    const n = norm(o.name)
    return n === t || n.includes(t) || t.includes(n)
  })
}

// Menu data sometimes mis-types a starch or grain (roasted potatoes, Tri-color Quinoa)
// as a 'vegetable', so a carb customization ("low carb"/"extra carb"/"no carb") has
// nothing to act on. Promote a starch-named vegetable to 'carb' — but only when the
// dish has NO real carb component, so a potato side next to a rice carb is left alone.
// Mirrors starch_as_carb in server/routers/airtable_import.py ("rice" is left out
// because "Cauliflower Rice" is a vegetable).
const STARCH_RE = /\b(potato\w*|yams?|quinoa|farro|couscous|orzo|barley|bulgur|freekeh|pasta|macaroni|penne|rigatoni|fusilli|linguine|fettuccine|gnocchi)\b/i
function normalizeMenuStarch(menu) {
  if (!menu?.dishes) return menu
  return {
    ...menu,
    dishes: menu.dishes.map(d => {
      const comps = d.components || []
      if (comps.some(c => c.component_type === 'carb')) return d
      return {
        ...d,
        components: comps.map(c =>
          c.component_type === 'vegetable' && STARCH_RE.test(c.name || '')
            ? { ...c, component_type: 'carb' }
            : c
        ),
      }
    }),
  }
}

const ACTION_BADGE = {
  extra: { label: 'extra', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  double: { label: 'extra', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  low: { label: 'low', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  none: { label: 'none', cls: 'bg-red-50 text-red-700 border-red-200' },
  swap: { label: 'swap', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  side: { label: 'on side', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
}

// ── One custom order, rendered as a plate-this card ─────────────────────────────
function CustomCard({ card }) {
  // Most orders are glass; a rare PLASTIC one is easy to grab the wrong container for
  // when plating fast. Tint the whole card amber so "this is plastic" reads at a
  // glance — on top of grouping plastics at the end + the PLASTIC label in the header.
  const isPlastic = /plastic/i.test(card.container || '')
  const heat = heatBanner(card.plate)
  return (
    <div className={`rounded-xl border overflow-hidden break-inside-avoid ${isPlastic ? 'bg-amber-50 border-amber-300' : 'bg-white border-havn-cream-300'}`}>
      <div className={`px-4 py-2.5 border-b flex items-center justify-between gap-2 ${isPlastic ? 'bg-amber-100 border-amber-200' : 'bg-havn-cream-50 border-havn-cream-200'}`}>
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-display font-semibold text-havn-green-900 truncate">{card.customer || 'Custom'}</span>
          {card.quantity > 1 && <span className="text-xs font-bold text-havn-brass-500">×{card.quantity}</span>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {card.flagged && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border bg-amber-50 text-amber-800 border-amber-300 flex items-center gap-0.5" title="AI flagged this order — verify against the note">
              <AlertTriangle className="w-3 h-3" /> verify
            </span>
          )}
          {card.container && (/plastic/i.test(card.container)
            ? <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-300">{card.container}</span>
            : <span className="text-[10px] uppercase tracking-wider text-havn-gray-500">{card.container}</span>
          )}
        </div>
      </div>

      {card.allergy && (
        <div className="mx-3 mt-3 px-3 py-2 bg-red-50 border-2 border-red-400 rounded-md text-xs font-bold text-red-800 flex items-start gap-1.5">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-px" /> <span>ALLERGY: {card.allergy}</span>
        </div>
      )}

      {heat && (
        <div className={`mx-3 mt-3 px-3 py-2 border-2 rounded-md text-xs ${heat.cls}`}>
          <span className="font-bold tracking-wider">{heat.label}</span>
          <span className="block mt-0.5">{heat.detail}</span>
          {heat.lookalikes.map((line) => (
            <span key={line} className="flex items-start gap-1 mt-1 font-semibold">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-px" /> {line}
            </span>
          ))}
        </div>
      )}

      {card.lines ? (
        <ul className="px-4 py-3 space-y-1.5">
          {card.lines.map((ln, j) => {
            const badge = ln.action !== 'regular' ? ACTION_BADGE[ln.action] : null
            return (
              <li key={j} className="flex items-center gap-2 text-sm">
                <span className="w-[68px] text-[10px] uppercase tracking-wider text-havn-gray-400 flex-shrink-0">{TYPE_LABEL[ln.type]}</span>
                {ln.action === 'swap' ? (
                  <span className="text-havn-charcoal">
                    <span className="line-through text-havn-gray-400">{ln.name}</span>{' → '}
                    <strong>{ln.to || `another ${TYPE_LABEL[ln.type].toLowerCase()}`}</strong>
                    {ln.onSide && <strong className="text-sky-800"> · on the side</strong>}
                    <span className="text-havn-gray-500"> · {fmtOz(ln.oz)}</span>
                  </span>
                ) : ln.action === 'none' ? (
                  <span className="text-red-700"><span className="line-through text-havn-gray-400">{ln.name}</span> · omit</span>
                ) : ln.action === 'side' ? (
                  <span className="text-sky-800">{ln.name} · <strong>on the side</strong> <span className="text-havn-gray-500">· {fmtOz(ln.oz)}</span></span>
                ) : (
                  <span className="text-havn-charcoal">{ln.name} · <strong className="tabular-nums">{fmtOz(ln.oz)}</strong></span>
                )}
                {badge && <span className={`ml-auto text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${badge.cls}`}>{badge.label}</span>}
              </li>
            )
          })}
        </ul>
      ) : (
        <div className="px-4 py-3 text-sm text-havn-gray-600 italic">{card.raw}</div>
      )}

      {card.raw && card.lines && <div className="px-4 pb-3 text-[11px] text-havn-gray-400 italic">note: “{card.raw}”</div>}
    </div>
  )
}

export default function Customs() {
  const { currentMenu, currentCity, cities } = useApp()
  const [menu, setMenu] = useState(null)
  const [customizations, setCustomizations] = useState([])
  const [loading, setLoading] = useState(true)
  const [printGroupId, setPrintGroupId] = useState(null) // dish id being printed (per-meal sheet)
  const { error } = useToastActions()

  useEffect(() => {
    if (!currentMenu) { setMenu(null); setCustomizations([]); setLoading(false); return }
    let cancelled = false
    async function load() {
      try {
        setLoading(true)
        const menuData = await menusAPI.get(currentMenu)
        if (cancelled) return
        setMenu(normalizeMenuStarch(menuData))
        const importData = await airtableAPI.getLatest(currentCity, currentMenu).catch(() => null)
        if (cancelled) return
        setCustomizations(Array.isArray(importData?.customizations) ? importData.customizations : [])
      } catch (err) {
        if (!cancelled) error('Failed to load customs: ' + err.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [currentMenu, currentCity])

  const dishByCat = useMemo(() => {
    const m = {}
    for (const d of (menu?.dishes || [])) if (d.category) m[d.category] = d
    return m
  }, [menu])

  // Swap replacements may ONLY come from components on THIS menu — never a city-wide
  // pool (which would surface ingredients from other weeks' menus).
  const menuPools = useMemo(() => {
    const pools = { protein: [], vegetable: [], carb: [], sauce: [] }
    const seen = new Set()
    for (const d of (menu?.dishes || [])) for (const c of (d.components || [])) {
      if (!pools[c.component_type]) continue
      const k = c.component_type + '::' + norm(c.name)
      if (seen.has(k)) continue
      seen.add(k)
      pools[c.component_type].push({ name: c.name, oz: Number(c.portion_oz) || 0 })
    }
    return pools
  }, [menu])

  function pickSwap(comp, to) {
    // Only ever called with a replacement buildLines has confirmed is available on THIS
    // week's menu (no-name and truly off-menu targets are omitted upstream). Use the
    // replacement's own menu portion when it names an exact component; when it's a
    // generic term the customer asked for ("Chicken", which resolves to but isn't
    // literally "Butter Chicken"), fall back to the removed component's portion — a 1:1
    // protein-for-protein swap at the same portion.
    const pool = menuPools[comp.component_type] || []
    const found = pool.find(o => norm(o.name) === norm(to))
    return { name: to, oz: found ? found.oz : Number(comp.portion_oz) || 0 }
  }

  function buildLines(dish, plate) {
    const comps = (dish.components || [])
      .filter(c => WEIGHED_TYPES.includes(c.component_type))
      .sort((a, b) => (TYPE_ORDER[a.component_type] || 9) - (TYPE_ORDER[b.component_type] || 9))
    const removeByName = {}
    ;(plate?.swaps || []).forEach(s => { if (s?.remove) removeByName[norm(s.remove)] = s })
    // Per-ingredient portion overrides (plate.item_portions): change the amount of ONE
    // named ingredient without touching the rest of its category — e.g. a salad "low
    // carb" trims just the chickpeas (the carb-dense item), not every vegetable. Each
    // entry is { name, portion: 'extra'|'low'|'none'|'regular', reason }.
    const itemPortionByName = {}
    ;(plate?.item_portions || []).forEach(p => { if (p?.name) itemPortionByName[norm(p.name)] = p })
    // Categories that have a targeted single-ingredient removal. If the AI ALSO set
    // that whole category to "none" (over-encoding "no mushrooms" as vegetable:none),
    // the "none" should only hit the swapped item — keep the OTHER components of that
    // category at their normal portion instead of dropping them too.
    const categoriesWithRemoval = new Set()
    comps.forEach(c => { if (removeByName[norm(c.name)]) categoriesWithRemoval.add(c.component_type) })
    return comps.map(c => {
      const std = Number(c.portion_oz) || 0
      const swap = removeByName[norm(c.name)]
      // A per-ingredient portion override wins for THIS component (unless it's being
      // swapped/removed, which is more specific), leaving the rest of its category at
      // normal portion — this is how "low carb" trims only the chickpeas on a salad.
      const itemP = !swap ? itemPortionByName[norm(c.name)] : null
      if (itemP) {
        const a = norm(itemP.portion) || 'regular'
        if (a === 'none') return { type: c.component_type, name: c.name, action: 'none', oz: 0, reason: itemP.reason }
        return { type: c.component_type, name: c.name, action: a, oz: (PORTION_RULE[a] || PORTION_RULE.regular)(std), reason: itemP.reason }
      }
      let action = norm(plate?.[c.component_type]) || 'regular'
      // A category "none" alongside targeted removals in that category is over-broad —
      // don't drop a component that wasn't itself singled out for removal.
      if (action === 'none' && !swap && categoriesWithRemoval.has(c.component_type)) action = 'regular'
      // "none" wins — omit the whole component; never substitute a replacement for it
      // (this is "no carb"/"no rice": give nothing, not a different carb).
      if (action === 'none') return { type: c.component_type, name: c.name, action: 'none', oz: 0 }
      // Honor a swap when the customer named a replacement, or when there's no
      // quantity change on this component. But if the AI ALSO set low/extra here
      // (e.g. "low carb, less potatoes" → carb:low PLUS a spurious remove-potato
      // swap), the quantity change is the real intent — apply it and drop the
      // redundant removal, which would otherwise substitute an unrelated
      // component (the wrong-carb "→ Cardamom Basmati Rice" bug).
      if (swap && (swap.to || action === 'regular')) {
        // Only honor a replacement that's actually available on THIS week's menu, but
        // match it loosely (see swapTargetOnMenu): a customer's generic named target
        // like "Chicken" IS on the menu even though the component is "Butter Chicken".
        // The AI sometimes names a truly off-menu item (e.g. "Cauliflower" on a week
        // without it) — that still matches nothing, so we OMIT rather than tell the
        // kitchen to plate something they don't have.
        const swapOnMenu = swapTargetOnMenu(menuPools[c.component_type], swap.to)
        if (!swap.to || !swapOnMenu) {
          // Removal with no usable replacement → OMIT it. Never substitute a random
          // same-type component (that's how a dairy-free order got a different DAIRY
          // sauce) or a truly off-menu one ("no mushrooms" → "Cauliflower"). A usable
          // replacement is a real on-menu component (exact, or a generic name the
          // customer asked for that resolves to one) named in swap.to.
          return { type: c.component_type, name: c.name, action: 'none', oz: 0, reason: swap.reason }
        }
        const rep = pickSwap(c, swap.to)
        // If the customer also reduced/increased this category (e.g. "gluten free
        // AND low carb" -> swap orzo for rice, served at half), apply that portion
        // rule to the substitute instead of its full portion.
        const oz = (action === 'low' || action === 'extra' || action === 'double')
          ? (PORTION_RULE[action] || PORTION_RULE.regular)(rep.oz)
          : rep.oz
        return { type: c.component_type, name: c.name, action: 'swap', to: rep.name, oz, reason: swap.reason, onSide: action === 'side' }
      }
      // regular / extra / low → same component, adjusted quantity
      return { type: c.component_type, name: c.name, action, oz: (PORTION_RULE[action] || PORTION_RULE.regular)(std) }
    })
  }

  const { groups, review, sideNotes } = useMemo(() => {
    const groups = []
    const byDish = {}
    const review = []
    const sideNotes = []
    for (const cust of customizations) {
      // Side notes are order-level instructions about the SIDES (chia/oats/broth) — not
      // the mains — so they have no plate card. Collect them for their own section so
      // they're visible whether or not the customer also has main customizations.
      if (cust.side_notes) sideNotes.push({ customer: cust.customer_name, note: cust.side_notes, allergy: cust.allergy_alert })
      const affected = Array.isArray(cust.affected_meals) ? cust.affected_meals : []
      // Only divert to review when there's genuinely nothing to plate. A flagged
      // order whose meals DO map to dishes still renders its plate cards (with a
      // ⚠ verify badge) — a defensive flag (often on a side-note) must not hide
      // otherwise-clear plate actions, or whole orders silently disappear.
      if (affected.length === 0) {
        // A pure side-note order (no mappable main) belongs in the Side notes section,
        // not the "Needs review" error list — it's a routine instruction, not a miss.
        if (!cust.side_notes && (cust.raw_instructions || cust.summary)) review.push(cust)
        continue
      }
      for (const m of affected) {
        // A meal the customer didn't order (quantity 0) is an empty AI placeholder —
        // e.g. a seafood_2 (shrimp) slot at quantity 0 that would otherwise render as
        // a phantom meal they never bought. Skip it. (A missing quantity means 1.)
        const qty = Number(m.quantity)
        if (Number.isFinite(qty) && qty <= 0) continue
        const dish = dishByCat[m.meal_type]
        if (!dish) { review.push({ ...cust, _why: `No dish for "${m.meal_type}"` }); continue }
        const card = {
          customer: cust.customer_name,
          container: cust.container_type,
          quantity: Number.isFinite(qty) && qty > 0 ? qty : 1,
          raw: m.customization || cust.raw_instructions,
          allergy: cust.allergy_alert,
          flagged: !!cust.flagged,
          plate: m.plate || null,
          lines: m.plate ? buildLines(dish, m.plate) : null,
        }
        if (!byDish[dish.dish_id]) { byDish[dish.dish_id] = { dish, cards: [] }; groups.push(byDish[dish.dish_id]) }
        byDish[dish.dish_id].cards.push(card)
      }
    }
    groups.sort((a, b) => MEAL_CATEGORIES.indexOf(a.dish.category) - MEAL_CATEGORIES.indexOf(b.dish.category))
    // Within each dish, group by container: all GLASS first, then all PLASTIC at the
    // end. Mixed glass/plastic is easy to miss when plating fast — you can hand a
    // plastic order a glass container without noticing. (Stable sort keeps order.)
    const isPlastic = (card) => /plastic/i.test(card.container || '')
    groups.forEach(g => g.cards.sort((a, b) => (isPlastic(a) ? 1 : 0) - (isPlastic(b) ? 1 : 0)))
    return { groups, review, sideNotes }
  }, [customizations, dishByCat, menuPools])

  const cityName = cities.find(c => c.id === currentCity)?.name || currentCity?.toUpperCase()
  const totalCards = groups.reduce((s, g) => s + g.cards.reduce((n, c) => n + (c.quantity || 1), 0), 0)
  const printGroup = groups.find(g => g.dish.dish_id === printGroupId) || null

  function printMeal(id) {
    setPrintGroupId(id)
  }

  // When a single-meal sheet opens, fire the print dialog once it has rendered.
  // The sheet is portaled to <body> and print CSS hides everything else, so ONLY
  // that one meal's customs print — never the whole page.
  useEffect(() => {
    if (printGroupId == null) return
    const t = setTimeout(() => window.print(), 150)
    return () => clearTimeout(t)
  }, [printGroupId])

  if (loading) return <div className="flex items-center justify-center py-24 text-havn-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
  if (!currentMenu) return <div className="py-24 text-center text-havn-gray-500">Select a menu to see customs.</div>

  return (
    <div className="max-w-5xl mx-auto pb-16">
      <style>{`@media print {
        body > *:not(.customs-print-overlay) { display: none !important; }
        .customs-print-overlay { position: static !important; overflow: visible !important; background: #fff !important; }
        .no-print { display: none !important; }
      }`}</style>

      <p className="text-sm text-havn-gray-600 mb-3">
        Each custom order as an exact plate-this card, grouped by dish. Portions come from this menu's
        plating; <strong>extra</strong> = +1 oz, <strong>low</strong> = half, <strong>none</strong> = omit. Swaps
        only use ingredients on this menu.
        <span className="text-havn-gray-400"> · {cityName} · {customizations.length} custom orders · {totalCards} meals</span>
      </p>

      {/* Per-meal print buttons — one printable sheet per dish for the line cook */}
      {groups.length > 0 && (
        <div className="no-print mb-6 flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-havn-gray-400 mr-1 flex items-center gap-1"><Printer className="w-3.5 h-3.5" /> Print sheet:</span>
          {groups.map(g => (
            <button key={g.dish.dish_id} onClick={() => printMeal(g.dish.dish_id)}
              className="px-3 py-1.5 text-sm rounded-lg border border-havn-cream-300 bg-white hover:border-havn-brass-400 hover:bg-havn-cream-50 transition-colors">
              {g.dish.name} <span className="text-havn-gray-400">({g.cards.reduce((n, c) => n + (c.quantity || 1), 0)})</span>
            </button>
          ))}
        </div>
      )}

      {groups.length === 0 && review.length === 0 && sideNotes.length === 0 && (
        <div className="py-16 text-center text-havn-gray-500">No customizations on this menu.</div>
      )}

      {groups.map(({ dish, cards }) => (
        <section key={dish.dish_id} className="mb-7">
          <div className="flex items-baseline gap-2 mb-2.5">
            <h2 className="font-display text-lg font-semibold text-havn-green-900">{dish.name}</h2>
            <span className="text-xs text-havn-gray-400">{cards.reduce((n, c) => n + (c.quantity || 1), 0)} custom meals</span>
            <button onClick={() => printMeal(dish.dish_id)} className="no-print ml-1 text-havn-gray-400 hover:text-havn-green-700" title={`Print ${dish.name} sheet`}>
              <Printer className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {cards.map((card, i) => <CustomCard key={i} card={card} />)}
          </div>
        </section>
      ))}

      {sideNotes.length > 0 && (
        <section className="mt-8">
          <div className="flex items-center gap-2 mb-2.5">
            <AlertCircle className="w-4 h-4 text-sky-500" />
            <h2 className="font-display text-lg font-semibold text-havn-green-900">Side notes</h2>
            <span className="text-xs text-havn-gray-400">{sideNotes.length} — chia / oats / broth instructions (no plate card)</span>
          </div>
          <div className="space-y-2">
            {sideNotes.map((s, i) => (
              <div key={i} className="bg-sky-50/50 border border-sky-200 rounded-lg px-4 py-2.5 text-sm">
                <span className="font-semibold text-havn-charcoal">{s.customer || 'Order'}</span>
                <div className="text-havn-gray-700">{s.note}</div>
                {s.allergy && <div className="mt-1 text-xs font-bold text-red-700">⚠️ ALLERGY: {s.allergy}</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      {review.length > 0 && (
        <section className="mt-8">
          <div className="flex items-center gap-2 mb-2.5">
            <AlertCircle className="w-4 h-4 text-amber-500" />
            <h2 className="font-display text-lg font-semibold text-havn-green-900">Needs review</h2>
            <span className="text-xs text-havn-gray-400">{review.length} — couldn’t map to a plate; check the note</span>
          </div>
          <div className="space-y-2">
            {review.map((r, i) => (
              <div key={i} className="bg-amber-50/40 border border-amber-200 rounded-lg px-4 py-2.5 text-sm">
                <span className="font-semibold text-havn-charcoal">{r.customer_name || 'Order'}</span>
                {r._why && <span className="text-xs text-amber-700"> · {r._why}</span>}
                <div className="text-havn-gray-600 italic">{r.summary || r.raw_instructions}</div>
                {r.allergy_alert && <div className="mt-1 text-xs font-bold text-red-700">⚠️ ALLERGY: {r.allergy_alert}</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Single-meal print sheet — portaled to <body> so print CSS hides the rest
          of the app and ONLY this meal's customs print. */}
      {printGroup && createPortal(
        <div className="customs-print-overlay fixed inset-0 z-[100] bg-white overflow-auto">
          <div className="max-w-4xl mx-auto p-6">
            <div className="no-print mb-4 flex items-center justify-between">
              <button onClick={() => window.print()} className="px-4 py-2 bg-havn-green-800 text-white rounded-lg text-sm font-medium flex items-center gap-2">
                <Printer className="w-4 h-4" /> Print
              </button>
              <button onClick={() => setPrintGroupId(null)} className="px-3 py-2 text-sm text-havn-gray-600 hover:text-havn-charcoal flex items-center gap-1"><X className="w-4 h-4" /> Close</button>
            </div>
            <div className="flex items-baseline gap-2 mb-1 border-b-2 border-havn-green-900 pb-1">
              <h1 className="font-display text-2xl font-bold text-havn-green-900">{printGroup.dish.name}</h1>
              <span className="text-sm text-havn-gray-500">— customs only</span>
            </div>
            <p className="text-xs text-havn-gray-500 mb-4">{cityName} · {menu?.name} · {printGroup.cards.length} order(s) · {printGroup.cards.reduce((n, c) => n + (c.quantity || 1), 0)} meals</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {printGroup.cards.map((card, i) => <CustomCard key={i} card={card} />)}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
